/**
 * AuthContext — provides authentication state and user role to the app
 */
import { createContext, useState, useEffect, useCallback } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { createUserProfile } from '../services/userService';

const DEFAULT_TRAINER_UID = import.meta.env.VITE_DEFAULT_TRAINER_UID || null;
import { createLogger } from '../utils/logger';
import { Sentry, reportError } from '../utils/sentry';
import { recordLogin } from '../services/loginStreakService';

const log = createLogger('AuthContext');

export const AuthContext = createContext(null);

// Dev mode: skip auth and act as trainer when running locally
const DEV_BYPASS = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS !== 'false';

const DEV_USERS = {
  trainer: {
    uid: 'test-trainer-uid',
    email: 'trainer@thrivecompanions.com',
    displayName: 'Amanda (dev)',
  },
  client: {
    uid: 'test-client-uid',
    email: 'sarah@example.com',
    displayName: 'Sarah (dev)',
  },
  beta: {
    uid: 'test-beta-uid',
    email: 'beta@example.com',
    displayName: 'Alex Beta (dev)',
  },
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(DEV_BYPASS ? DEV_USERS.trainer : null);
  const [role, setRole] = useState(DEV_BYPASS ? 'trainer' : null);
  const [isBeta, setIsBeta] = useState(false);
  const [loading, setLoading] = useState(true);

  const toggleDevRole = useCallback(() => {
    if (!DEV_BYPASS) return;
    setLoading(true);
    const cycle = { trainer: 'client', client: 'beta', beta: 'trainer' };
    const nextKey = cycle[role] || 'trainer';
    const newRole = nextKey === 'beta' ? 'client' : nextKey;
    setRole(newRole);
    setIsBeta(nextKey === 'beta');
    setUser(DEV_USERS[nextKey]);
    log.info('Dev role switched', { newRole: nextKey });
  }, [role, isBeta]);

  // In dev bypass mode, sign into Firebase Auth with the test account
  // so Firestore security rules see a real auth token.
  useEffect(() => {
    if (DEV_BYPASS) {
      const devUser = DEV_USERS[role] || DEV_USERS.trainer;
      const password = 'testpass123';
      signInWithEmailAndPassword(auth, devUser.email, password)
        .then(() => {
          log.info('Dev auth: signed into Firebase as', { email: devUser.email });
          setLoading(false);
        })
        .catch((err) => {
          log.warn('Dev auth: Firebase sign-in failed (emulators running?)', { error: err.message });
          setLoading(false);
        });
    }
  }, [role]);

  useEffect(() => {
    if (DEV_BYPASS) {
      log.info('Dev auth bypass active — signed in as trainer');
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        log.info('User authenticated', { uid: firebaseUser.uid });

        // Fetch role from custom claims
        const tokenResult = await firebaseUser.getIdTokenResult();
        const userRole = tokenResult.claims.role || 'client';
        const betaFlag = tokenResult.claims.isBeta === true;

        // Fetch user profile from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          let profile;

          if (userDoc.exists()) {
            profile = userDoc.data();
          } else if (userRole === 'client') {
            // Recovery: Auth account exists but Firestore user doc is missing.
            // This can happen if registration partially failed after Auth creation.
            log.warn('Missing user doc — recovering', { uid: firebaseUser.uid });
            try {
              await createUserProfile({
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                displayName: firebaseUser.displayName || 'Client',
                role: 'client',
                trainerId: DEFAULT_TRAINER_UID,
                isBeta: betaFlag,
              });
              const recoveredDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
              profile = recoveredDoc.exists() ? recoveredDoc.data() : {};
              log.info('User doc recovered successfully', { uid: firebaseUser.uid });
            } catch (recoverErr) {
              reportError(recoverErr, { context: 'user_doc_recovery', uid: firebaseUser.uid });
              profile = {};
            }
          } else {
            profile = {};
          }

          setUser({ ...firebaseUser, ...profile });
          setRole(userRole);
          setIsBeta(betaFlag || profile.isBeta === true);
          Sentry.setUser({ id: firebaseUser.uid, email: firebaseUser.email });

          // Track login streak (fire-and-forget, clients only)
          if (userRole === 'client') {
            recordLogin(firebaseUser.uid);
          }
        } catch (err) {
          log.error('Failed to fetch user profile', { uid: firebaseUser.uid, error: err.message });
          setUser(firebaseUser);
          setRole(userRole);
          setIsBeta(betaFlag);
          Sentry.setUser({ id: firebaseUser.uid, email: firebaseUser.email });
        }
      } else {
        log.info('User signed out');
        Sentry.setUser(null);
        setUser(null);
        setRole(null);
        setIsBeta(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const refreshUser = useCallback(async () => {
    const current = auth.currentUser;
    if (!current) return;
    await current.reload();
    try {
      const userDoc = await getDoc(doc(db, 'users', current.uid));
      const profile = userDoc.exists() ? userDoc.data() : {};
      setUser({ ...current, ...profile });
    } catch {
      setUser(current);
    }
  }, []);

  const value = {
    user,
    role,
    isBeta,
    loading,
    isAuthenticated: !!user,
    isTrainer: role === 'trainer' || role === 'admin',
    isClient: role === 'client',
    isAdmin: role === 'admin',
    refreshUser,
    toggleDevRole: DEV_BYPASS ? toggleDevRole : undefined,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
